import { Grid } from "@mui/material";
import Checkbox from "@shared/components/Checkbox";
import DateRangeField from "@shared/components/DateRangeField";
import { DateRange } from "@shared/components/DateRangePicker";
import { ISelectOption } from "@shared/components/ISelectOption";
import MaterialAutocompleteField from "@shared/components/MaterialAutocomplete/MaterialAutocompleteField";
import MaterialDateRangePicker from "@shared/components/MaterialDateRangePicker";
import {
  METRICS_TO_DISPLAY,
  SHERLOCK_CHANGES,
  SherlockChangeType,
  SherlockMetricType,
} from "@shared/http/apiTypes/sherlockApiTypes";
import React from "react";
import classes from "./InvestigationSettings.module.css";

type InvestigationSettingsProps = {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  organizationOptions: ISelectOption[];
  campaignOptions: ISelectOption[];
  selectedOrganization: string;
  onOrganizationSelect: (val: string) => void;
  selectedCampaign: string;
  onCampaignChange: (val: string) => void;
  selectedChanges: SherlockChangeType[];
  onChangesToDisplayChange: (val: SherlockChangeType) => void;
  selectedMetric: SherlockMetricType;
  onMetricChange: (val: SherlockMetricType) => void;
};

function InvestigationSettings({
  organizationOptions,
  campaignOptions,
  dateRange,
  setDateRange,
  selectedOrganization,
  onOrganizationSelect,
  onCampaignChange,
  selectedCampaign,
  selectedChanges,
  onChangesToDisplayChange,
  selectedMetric,
  onMetricChange,
}: InvestigationSettingsProps) {
  const handleDateRangeChange = (dateRange: DateRange) => {
    setDateRange(dateRange);
  };

  const metricOptions = Object.entries(METRICS_TO_DISPLAY).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  return (
    <>
      <Grid container spacing={2} display="flex" justifyContent="center">
        <Grid item>
          <div className={classes.investigationSettingsTitle}>Date selection</div>

          <Grid marginTop={2}>
            <MaterialDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              minDate={sixMonthsAgo}
              renderTriggerButton={(dateRange, onClick) => (
                <DateRangeField
                  dateRange={dateRange}
                  onClick={onClick}
                  label="Selected Period"
                  variant="standard"
                  style={{ minWidth: "220px" }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid item>
          <div className={classes.investigationSettingsTitle}>Campaign selection</div>

          <Grid container direction="row">
            <Grid marginTop={1} marginRight={2} width={400}>
              <MaterialAutocompleteField
                fullWidth
                value={selectedOrganization}
                options={organizationOptions}
                onChange={(val: string) => onOrganizationSelect(val)}
                name="orgs"
                id="orgs"
                label="Organization"
                errors={[]}
              />
            </Grid>

            <Grid item marginTop={1} marginRight={2} width={400}>
              <MaterialAutocompleteField
                isLoading={selectedOrganization.length > 0 && campaignOptions.length === 0}
                fullWidth
                value={selectedCampaign}
                options={campaignOptions}
                onChange={(val: string) => onCampaignChange(val)}
                name="campaign"
                id="campaign"
                label="Campaign"
                errors={[]}
                emptyValueIsValid
                disabled={!selectedOrganization}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid margin={2} marginRight={2}>
          <div className={classes.investigationSettingsTitle}>Metric To Display</div>

          <Grid item marginTop={1}>
            <MaterialAutocompleteField
              fullWidth
              value={selectedMetric}
              options={metricOptions}
              onChange={(val: SherlockMetricType) => onMetricChange(val)}
              name="metric"
              id="metric"
              label="Metric"
              errors={[]}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid marginTop={4} marginBottom={2} textAlign={"center"}>
        <div className={classes.investigationSettingsTitle}>Changes To Display</div>
      </Grid>

      <Grid container direction="row" justifyContent="center" marginLeft={4} marginRight={4}>
        {Object.entries(SHERLOCK_CHANGES)
          .sort((a, b) => (a[1] > b[1] ? 1 : b[1] > a[1] ? -1 : 0))
          .map(([key, value]) => {
            return (
              <Checkbox
                key={key}
                label={value}
                onChange={() => onChangesToDisplayChange(key as SherlockChangeType)}
                value={selectedChanges.includes(key as SherlockChangeType)}
              />
            );
          })}
      </Grid>
    </>
  );
}

export default React.memo(InvestigationSettings);
